import { Button, Input } from '@breetometer/components';
import { InfoIcon } from 'BreetAssets';
import { Tooltip, TooltipContent } from 'BreetComponents';
import { capitalizeFirstLetter, type IApiResponseError, showToast } from 'BreetHelpers';
import { useFormData } from 'BreetHooks';
import { useAddBeneficiaryMutation, useValidateBankMutation } from 'BreetRedux';
import { type ChangeEvent, type FormEvent, useCallback, useState } from 'react';

import { BankDetailsSchema, defaultValidatedBankDetails, type IDialogAddBeneficiary } from './AddBeneficiary.helpers';
import BankDetailsForm from './components/BankDetailsForm';
import NoBeneficiary from './components/NoBeneficiary';

const AddBeneficiary = ({ noBeneficiary, intent, onSuccess }: IDialogAddBeneficiary) => {
	const [validateBank, { isLoading: isValidateBankLoading }] = useValidateBankMutation();

	const [addBeneficiary, { isLoading: isAddNewBeneficiaryLoading }] = useAddBeneficiaryMutation();

	const formDataContextProps = useFormData(BankDetailsSchema, undefined, { mode: 'onChange' });

	const {
		hookFormProps: {
			setValue,
			watch,
			setError,
			clearErrors,
			reset,
			register,
			formState: { errors },
		},
		isFormCompleted,
	} = formDataContextProps;

	const bankId = watch('bankId');
	const accountNumber = watch('accountNumber');
	const validatedAccountName = watch('validatedBankDetails.accountName');
	const validatedAccountNumber = watch('validatedBankDetails.accountNumber');
	const narration = watch('narration');

	const fetchBankDetails = useCallback(() => {
		if (!bankId) return;

		if (!accountNumber || accountNumber.length < 10) {
			setValue('validatedBankDetails', defaultValidatedBankDetails);
			return;
		}

		clearErrors('bankId');

		validateBank({ id: bankId, accountNumber })
			.unwrap()
			.then((res) => {
				if (res.data) setValue('validatedBankDetails', res.data, { shouldValidate: true });
			})
			.catch((error: unknown) => {
				const errorData = error as IApiResponseError | undefined;

				const errorDataMessage = errorData?.data.message;
				setError('bankId', { message: errorDataMessage });
			});
	}, [accountNumber, bankId, clearErrors, setError, setValue, validateBank]);

	const onHandleBankChange = useCallback(
		(valueArg: string) => {
			clearErrors('bankId');
			setValue('bankId', valueArg);

			if (bankId !== valueArg) {
				setValue('validatedBankDetails', defaultValidatedBankDetails);
			}
		},
		[bankId, clearErrors, setValue]
	);

	const handleAccountNumber = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			clearErrors(['bankId', 'accountNumber']);
			const value = e.target.value.replace(/\D/g, '');

			setValue('accountNumber', value);

			if (!value || value.length < 10) {
				setValue('validatedBankDetails', defaultValidatedBankDetails);
			}
		},
		[clearErrors, setValue]
	);

	const addNewBeneficiary = useCallback(async () => {
		const data = {
			accountNumber: validatedAccountNumber,
			id: bankId,
			narration,
		};

		const res = await addBeneficiary(data)
			.unwrap()
			.then((response) => {
				showToast('success', 'bank added successfully 🎉');
				return response;
			});
		return res;
	}, [addBeneficiary, bankId, narration, validatedAccountNumber]);

	const resetForm = useCallback(() => {
		reset({ accountNumber: '', bankId: '', validatedBankDetails: defaultValidatedBankDetails });
	}, [reset]);

	const handleSubmit = useCallback(
		(e: FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			addNewBeneficiary()
				.then(() => {
					resetForm();
					onSuccess?.();
				})
				.catch((reason: unknown) => {
					const {
						data: { message },
					} = reason as IApiResponseError;
					showToast('error', message);
				});
		},
		[addNewBeneficiary, onSuccess, resetForm]
	);

	const [isAddAccount, setIsAddAccount] = useState<boolean>(false);

	const addAccount = useCallback(() => {
		setIsAddAccount(true);
	}, []);

	return (
		<>
			{noBeneficiary && !isAddAccount ? (
				<NoBeneficiary
					intent={intent}
					addAccount={addAccount}
				/>
			) : (
				<form
					onSubmit={handleSubmit}
					className='addbeneficiary'
					data-testid='add-beneficiary'
					noValidate
				>
					<BankDetailsForm
						handleAccountNumber={handleAccountNumber}
						onHandleBankId={onHandleBankChange}
						validatedAccountName={validatedAccountName}
						value={accountNumber}
						fetchBankDetails={fetchBankDetails}
						isFetchingBankDetails={isValidateBankLoading}
						bankErrorMessage={errors.bankId?.message}
					/>

					<div className='narration-input'>
						<Input
							id=''
							label='Default Narration'
							widthType='full'
							{...register('narration')}
							errorMessage={capitalizeFirstLetter(errors.narration?.message)}
							required
						/>
						<div className='narration-input-icon'>
							<Tooltip tipElement={<TooltipContent content='This narration will be used for withdrawals and auto settlement.' />}>
								<InfoIcon className='info-icon svg-dark' />
							</Tooltip>
						</div>
					</div>

					<div className='addbeneficiary_cta'>
						<Button
							label='Add Account'
							type='submit'
							isLoading={isAddNewBeneficiaryLoading}
							disabled={!validatedAccountName || !isFormCompleted}
						/>
					</div>
				</form>
			)}
		</>
	);
};

export default AddBeneficiary;
